import PublicService from '@/services/public.service';
class ValidationService {
    constructor() { }
    /**
   * Messages d'erreur de validation
   */
    static get VALIDATION_MESSAGES() {
        return {
            EMPTY_CART: "Votre panier est vide, vous ne pouvez pas valider votre commande",
            NO_INSCRIPTIONS: "Votre panier est vide, vous ne pouvez pas valider votre commande",
            MISSING_EMAIL: "Une erreur est survenu lors de l'inscription d'un participant, vous devez renseigner un email pour chaque participant",
            EMPTY_EMAIL: "Une erreur est survenu lors de l'inscription d'un participant, vous devez renseigner un email pour chaque participant",
            EMPTY_FIELDS: "Une erreur est survenu lors de l'inscription d'un participant, veuillez vérifier les informations saisies à l'étape précedente",
            API_ERROR: "Une erreur est survenue"
        };
    }

    /**
 * Vérifie les disponibilités d'une réservation via l'API
 * 
 * @param {Object} booking - L'état de la réservation
 * @param {Object} groupe - L'état du groupe (optionnel)
 * @returns {Promise<Object>} Résultat de la vérification
 * @returns {boolean} status - True si les disponibilités sont OK
 * @returns {string[]} errors - Liste des erreurs éventuelles
 * @returns {boolean} need_tarif_update - Indique si les tarifs doivent être mis à jour
 * @returns {Object} refreshed_tarifs - Les tarifs rafraîchis (si besoin)
 */
    async checkAvailabilities(booking, groupe = null) {
        try {
            const res = await PublicService.checkCommandeAvailabilities(booking, groupe);

            if (!res) {
                return {
                    status: false,
                    errors: [ValidationService.VALIDATION_MESSAGES.API_ERROR],
                    need_tarif_update: false,
                    refreshed_tarifs: null
                };
            }

            return {
                status: res.checked || false,
                errors: res.errors || [],
                need_tarif_update: res.need_tarif_update || false,
                refreshed_tarifs: res.refreshed_tarifs || null
            };
        } catch (error) {
            console.error('Erreur lors de la vérification des disponibilités:', error);
            return {
                status: false,
                errors: [ValidationService.VALIDATION_MESSAGES.API_ERROR],
                need_tarif_update: false,
                refreshed_tarifs: null
            };
        }
    }

    /**
   * Valide localement l'état d'une réservation
   * Vérifie que le panier n'est pas vide et que toutes les inscriptions ont les informations requises
   * 
   * @param {Object} booking - L'état de la réservation à valider
   * @param {Function} options.computeCart - Fonction pour recalculer le panier (optionnel)
   * @param {Function} options.commitToLocalStorage - Fonction pour sauvegarder (optionnel)
   * @returns {Object} Résultat de la validation
   * @returns {boolean} status - True si la validation est réussie
   * @returns {string} message - Message d'erreur si la validation échoue
   */
    validateBooking(booking) {
        const response = {
            status: true,
            message: ""
        };

        // Vérifier que le panier n'est pas vide
        if (!booking.nb_articles || booking.nb_articles === 0) {
            response.status = false;
            response.message = ValidationService.VALIDATION_MESSAGES.EMPTY_CART;
            return response;
        }

        // Vérifier qu'il y a des inscriptions
        if (!booking.inscriptions || booking.inscriptions.length === 0) {
            response.status = false;
            response.message = ValidationService.VALIDATION_MESSAGES.NO_INSCRIPTIONS;
            return response;
        }

        // Valider chaque inscription
        for (let i = 0; i < booking.inscriptions.length; i++) {
            const inscription = booking.inscriptions[i];
            const inscriptionValidation = this.validateInscription(inscription);

            if (!inscriptionValidation.status) {
                response.status = false;
                response.message = inscriptionValidation.message;
                return response;
            }
        }

        return response;
    }

    /**
 * Valide une inscription individuelle
 * 
 * @param {Object} inscription - L'inscription à valider
 * @returns {Object} Résultat de la validation
 * @returns {boolean} status - True si la validation est réussie
 * @returns {string} message - Message d'erreur si la validation échoue
 */
    validateInscription(inscription) {
        const response = {
            status: true,
            message: ""
        };

        // Vérifier que le coureur existe et a des champs
        if (!inscription.coureur || !inscription.coureur.champs || !inscription.coureur.champs.mail) {
            response.status = false;
            response.message = ValidationService.VALIDATION_MESSAGES.MISSING_EMAIL;
            return response;
        }

        // Vérifier qu'il y a au moins un champ rempli
        if (Object.keys(inscription.coureur.champs).length === 0) {
            response.status = false;
            response.message = ValidationService.VALIDATION_MESSAGES.EMPTY_FIELDS;
            return response;
        }

        // Vérifier que l'email n'est pas vide
        if (!inscription.coureur.champs.mail.value || inscription.coureur.champs.mail.value === "") {
            response.status = false;
            response.message = ValidationService.VALIDATION_MESSAGES.EMPTY_EMAIL;
            return response;
        }

        return response;
    }

    /**
 * Vérifie qu'un dossard bourse n'est pas déjà dans le panier d'un autre utilisateur
 * 
 * @param {Object} dossard_bourse - Le dossard bourse à vérifier
 * @returns {Promise<Object>} Résultat de la vérification
 * @returns {boolean} status - True si le dossard est disponible
 * @returns {string[]} errors - Liste des erreurs éventuelles
 */
    async checkDossardBourseAvailability(dossard_bourse) {
        try {
            if (!dossard_bourse) {
                return {
                    status: false,
                    errors: ["Aucun dossard bourse fourni"]
                };
            }

            const res = await PublicService.checkDossardBourseNotInAnotherCart(dossard_bourse);

            if (!res) {
                return {
                    status: false,
                    errors: [ValidationService.VALIDATION_MESSAGES.API_ERROR]
                };
            }

            return {
                status: res.checked || false,
                errors: res.errors || []
            };
        } catch (error) {
            console.error('Erreur lors de la vérification du dossard bourse:', error);
            return {
                status: false,
                errors: [ValidationService.VALIDATION_MESSAGES.API_ERROR]
            };
        }
    }

    /**
 * Valide une réservation avant l'affichage du formulaire de paiement
 * Combine la validation locale et la vérification des disponibilités selon le contexte
 * 
 * @param {Object} booking - L'état de la réservation
 * @param {Object} context - Le contexte de validation
 * @param {boolean} context.isBookingDossardBourse - Indique si c'est une réservation de dossard bourse
 * @param {boolean} context.isBookingVip - Indique si c'est une réservation VIP
 * @param {boolean} context.isBookingAdmin - Indique si c'est une réservation admin
 * @param {boolean} context.isBookingGroupeOpenCourse - Indique si c'est une inscription groupe ouverte
 * @param {Object} context.groupe - L'état du groupe (optionnel)
 * @param {Object} context.dossard_bourse - Le dossard bourse (optionnel)
 * @param {Function} context.computeCart - Fonction pour recalculer le panier (optionnel)
 * @param {Function} context.commitToLocalStorage - Fonction pour sauvegarder (optionnel)
 * @returns {Promise<Object>} Résultat de la validation
 */
    async validateForPayment(booking, context = {}) {
        const {
            isBookingDossardBourse = false,
            isBookingVip = false,
            isBookingAdmin = false,
            isBookingGroupeOpenCourse = false,
            groupe = null,
            dossard_bourse = null
        } = context;

        // Si VIP, Admin ou Groupe Open Course, pas de validation nécessaire
        if (isBookingVip || isBookingAdmin || isBookingGroupeOpenCourse) {
            return {
                status: true,
                errors: []
            };
        }

        // Validation spécifique pour dossard bourse
        if (isBookingDossardBourse && dossard_bourse) {
            return await this.checkDossardBourseAvailability(dossard_bourse);
        }
        // Validation standard avec vérification des disponibilités
        return await this.checkAvailabilities(booking, groupe);
    }


}

export default new ValidationService();